'use client';

import { useRouter } from 'next/navigation';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { FullScreenLoader } from '@terra/ui-design-system';
import { useRequireTorchSupportedTenant } from '../hooks/useRequireTorchSupportedTenant';

type AuthGuardProps = {
  children: ReactNode;
};

export function AuthGuard({ children }: AuthGuardProps) {
  const router = useRouter();
  const [isElectronReady, setIsElectronReady] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI) {
      return;
    }

    if (window.electronAPI?.onElectronReady) {
      const cleanup = window.electronAPI.onElectronReady(() => {
        setIsElectronReady(true);
      });

      const timeout = setTimeout(() => {
        if (window.electronAPI?.auth) {
          setIsElectronReady(true);
        } else {
          console.error('Electron API failed to initialize');
          router.push('/sign-in');
        }
      }, 10000);

      return () => {
        cleanup?.();
        clearTimeout(timeout);
      };
    }

    const timeout = setTimeout(() => {
      console.error('Electron API not available');
      router.push('/sign-in');
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [router]);

  useEffect(() => {
    const checkAuth = async () => {
      if (!isElectronReady) return;

      try {
        const result = await window.electronAPI.auth.isAuthenticated();

        setIsAuthenticated(result.authenticated);

        if (!result.authenticated) {
          router.push('/sign-in');
        }
      } catch (error) {
        console.error('Failed to check authentication:', error);
        setIsAuthenticated(false);
        router.push('/sign-in');
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, [isElectronReady, router]);

  useRequireTorchSupportedTenant({
    enabled: isElectronReady && isAuthenticated && !isCheckingAuth,
  });

  if (!isElectronReady || isCheckingAuth) {
    return <FullScreenLoader />;
  }

  if (!isAuthenticated) {
    return <FullScreenLoader />;
  }

  return children;
}
